// frontend/src/components/common/ActionMenu.jsx
//
// The SaaS app's one popup action menu. Every menu an action affordance opens —
// a grid row's ⋯, a row's [Download ▼], a page toolbar's [Export ▼] — renders
// through this component, so all of them share one surface, one type scale and
// one set of item semantics. A new menu calls this; it never mounts its own
// <Menu>, which would fall through to the app-wide `.MuiMenuItem-root` rule
// that dresses <Select> options.
//
// The TRIGGER stays with the caller because the affordances are genuinely
// different (a kebab icon sized to a grid cell, a compact outlined button in a
// cell, a full-size button in a toolbar) — but its menu wiring does not: spread
// the `props` handed to renderTrigger and the click handler and the ARIA
// relationship come with it. The second argument, `{ open }`, is for a trigger
// that draws its own state — a button's DisclosureCaret.
//
// Each entry: { key, label, icon?, onClick, disabled?, danger?, separatorBefore? }
//   label — the entry's text, and a string: the menu sorts on it.
//   separatorBefore — starts a new group. Ignored on the first rendered entry,
//     so a caller may drop the entry above it without leaving a divider hanging
//     at the top.
//   danger — destructive; painted error.main.
//
// Entries are presented in the merchant's alphabetical order, not the order the
// caller happened to push them in — one scan rule for every menu in the app.
// Sorting is WITHIN a group: `separatorBefore` still decides where the groups
// fall and the caller still decides their order, because sorting across a
// separator would merge two lists it deliberately split.
// `onClick` takes no arguments: an entry closes over whatever row or scope it
// acts on, which keeps this component free of any caller's data model.
//
// Labels are bare text nodes, NOT <ListItemText>: that wraps its child in a
// Typography whose `body1` is 16px and whose own rule outranks the font-size
// inherited from the MenuItem, so a menu using it renders larger than every
// other menu in the app. Plain text
// picks up `.app-action-menu .MuiMenuItem-root` (styles/mui-overrides.css),
// which is where this menu's type and density live.

import { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider, ListItemIcon, Menu, MenuItem } from '@mui/material';

/**
 * Split the entries on their group separators, alphabetise each group, and
 * re-stamp `separatorBefore` onto whichever entry now opens it — the divider
 * belongs to the group, not to the entry that happened to declare it. The first
 * group can never carry one, which is what keeps a menu from opening on a rule
 * when the caller dropped the group above.
 */
function sortedByLabel(items, collator) {
  const groups = [];
  items.forEach((item, index) => {
    if (groups.length === 0 || (item.separatorBefore && index > 0)) groups.push([]);
    groups[groups.length - 1].push(item);
  });

  return groups.flatMap((group, groupIndex) => [...group]
    .sort((a, b) => collator.compare(a.label, b.label))
    .map((item, indexInGroup) => ({
      ...item,
      separatorBefore: groupIndex > 0 && indexInGroup === 0,
    })));
}

export default function ActionMenu({ items, renderTrigger }) {
  const { i18n } = useTranslation();
  const [anchor, setAnchor] = useState(null);
  // Kept when the menu closes, so the closing fade stays where the menu was.
  const [side, setSide] = useState('left');

  // Alphabetical to the MERCHANT: code-point order puts Ł after Z for a Polish
  // reader and mis-files every accented label. Built once per language because
  // a collator costs more than the two-to-five comparisons it then serves.
  const collator = useMemo(() => new Intl.Collator(i18n.language), [i18n.language]);

  // The TRIGGER stops propagation because it is a real DOM descendant of
  // whatever it sits in: inside an AG Grid action cell that is a cell the grid
  // may handle itself (a grid that binds onCellClicked for drill-down).
  //
  // Dismissal does NOT, and must not grow a guard "to match": MUI renders the
  // menu through a Portal, into the tab's overlay host inside the shell
  // (tabs/TabOverlayScope.jsx) or onto document.body outside it — either way a
  // click on an item or the backdrop lands outside the grid's subtree.
  //
  // The menu lines up with the edge of the trigger on the trigger's side of the
  // window, so it opens toward the middle of the page: a left-aligned menu from
  // a button at the right edge is pushed back by the window and loses the edge
  // it dropped from.
  const open = useCallback((e) => {
    e.stopPropagation();
    const { left, right } = e.currentTarget.getBoundingClientRect();
    setSide(left + right > window.innerWidth ? 'right' : 'left');
    setAnchor(e.currentTarget);
  }, []);
  const close = useCallback(() => setAnchor(null), []);

  return (
    <>
      {renderTrigger({
        onClick: open,
        'aria-haspopup': 'menu',
        'aria-expanded': anchor ? 'true' : 'false',
      }, { open: !!anchor })}
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: side }}
        transformOrigin={{ vertical: 'top', horizontal: side }}
        slotProps={{
          paper: {
            className: 'app-action-menu',
            // The menu is never narrower than what opened it: a popup that
            // undercuts its own trigger reads as a detached tooltip.
            style: anchor ? { minWidth: anchor.offsetWidth } : undefined,
          },
        }}
      >
        {sortedByLabel(items, collator).map((item) => (
          <Fragment key={item.key}>
            {item.separatorBefore && <Divider />}
            <MenuItem
              disabled={item.disabled}
              onClick={() => {
                close();
                item.onClick();
              }}
              sx={item.danger ? { color: 'error.main' } : undefined}
            >
              {/* ListItemIcon carries its own palette color, so `danger` has to
                  be restated here — the MenuItem's color does not cascade. */}
              {item.icon && (
                <ListItemIcon sx={item.danger ? { color: 'error.main' } : undefined}>
                  {item.icon}
                </ListItemIcon>
              )}
              {item.label}
            </MenuItem>
          </Fragment>
        ))}
      </Menu>
    </>
  );
}
